import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Link } from 'react-router-dom';
import useSEO from '@/hooks/useSEO';

const Terms = () => {
  useSEO({
    title: 'Terms of Service — EpisodeKit',
    description: 'EpisodeKit terms of service. Account, billing, content rights, and the rules of the road for using the platform.',
    path: '/terms',
    image: '/og/episodekit-og-terms.jpg',
    index: true,
  });
  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="noise-overlay" />
      <Navbar />

      <section className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.h1 
            className="text-4xl md:text-5xl font-unbounded font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Terms of Service
          </motion.h1>
          
          <motion.div
            className="prose prose-invert max-w-none"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <p className="text-neutral-400 mb-8">
              Last Updated: December 19, 2025
            </p>

            <div className="space-y-6 text-neutral-300">
              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">1. Description of Service</h2>
                <p>
                  EpisodeKit provides automated transcription and AI-powered content generation for podcast audio files. We use advanced AI models to generate transcripts, show notes, blog posts, social media snippets, and other podcast-related content. All AI-generated content is provided as-is and may require human review before use.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">2. Acceptance of Terms</h2>
                <p>
                  By accessing or using EpisodeKit, you agree to be bound by these Terms of Service and our <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>. If you do not agree to these terms, please do not use our service. Your continued use of the service after any modifications to these Terms constitutes acceptance of those changes.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">3. User Accounts</h2>
                <p>
                  You are responsible for:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Maintaining the confidentiality of your account credentials</li>
                  <li>All activities that occur under your account</li>
                  <li>Notifying us immediately of any unauthorized access or security breaches</li>
                  <li>Providing accurate, current, and complete information during registration and use</li>
                  <li>Keeping your account information up to date</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">4. Subscription Plans & Billing</h2>
                <p>
                  <strong>Subscription Plans:</strong> We offer Free, Basic, Pro, and Unlimited subscription plans with different usage limits and features as described on our pricing page.
                </p>
                <p className="mt-3">
                  <strong>Billing and Payment Processing:</strong> All paid subscriptions are processed through Lemon Squeezy, which acts as our Merchant of Record. Lemon Squeezy handles:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Subscription billing and recurring payments</li>
                  <li>Tax calculation and collection (including VAT for EU customers)</li>
                  <li>Payment processing and transaction security</li>
                  <li>Refunds and payment disputes</li>
                </ul>
                <p className="mt-3">
                  <strong>Important:</strong> EpisodeKit does NOT collect, process, or store any credit card details or payment information. All payment data is handled securely by Lemon Squeezy in accordance with industry standards and applicable payment regulations.
                </p>
                <p className="mt-3">
                  <strong>Billing Cycle:</strong> Charges occur at the start of each billing cycle (monthly). You authorize Lemon Squeezy to charge your selected payment method on a recurring basis.
                </p>
                <p className="mt-3">
                  <strong>Cancellation:</strong> You may cancel your subscription at any time via the Lemon Squeezy customer portal link provided in your receipt email or order page. Cancellation takes effect at the end of the current billing period. You will retain access to paid features until the end of your billing cycle.
                </p>
                <p className="mt-3">
                  <strong>Refunds:</strong> Refunds are handled according to Lemon Squeezy's refund policy. Generally, we do not provide refunds for partial months of service. For refund requests, please contact Lemon Squeezy support or email us at support@episodekit.net. Nothing in this section affects mandatory consumer rights under applicable law.
                </p>
                <p className="mt-3">
                  <strong>Plan Changes:</strong> Upgrading takes effect immediately, and you will be charged a prorated amount for the remainder of the billing cycle. Downgrading takes effect at the next billing cycle.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">5. Usage Limits & Fair Use</h2>
                <p>
                  All plans have monthly usage limits measured in minutes of audio processed. The limits are:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Free:</strong> 60 minutes per month</li>
                  <li><strong>Basic:</strong> 300 minutes per month</li>
                  <li><strong>Pro:</strong> 900 minutes per month</li>
                  <li><strong>Unlimited:</strong> 3,000 minutes per month</li>
                </ul>
                <p className="mt-3">
                  Usage resets at the start of each calendar month. Usage is counted based on the full duration of each uploaded episode. Longer episodes consume more of your monthly minutes allowance.
                </p>
                <p className="mt-3">
                  All usage is subject to our <Link to="/fair-use" className="text-primary hover:underline">Fair Use Policy</Link>. The Unlimited plan, despite its name, is intended for normal human-driven podcast workflows and is NOT intended for automated, continuous, or bulk processing operations. We reserve the right to enforce usage limits and take action against accounts that violate the Fair Use Policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">6. User Content & Rights</h2>
                <p>
                  <strong>Your Content:</strong> You retain all rights to the audio files you upload. By using our service, you grant us a limited, non-exclusive license to process your content solely to provide the service and generate the requested output.
                </p>
                <p className="mt-3">
                  <strong>Generated Content:</strong> You own all AI-generated content (transcripts, show notes, blog posts, social media snippets, etc.) produced from your uploads.
                </p>
                <p className="mt-3">
                  <strong>Content Restrictions:</strong> You may not upload content that:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>You don't own or lack proper rights to process</li>
                  <li>Violates copyright, trademark, or other intellectual property rights</li>
                  <li>Contains illegal, harmful, defamatory, or offensive material</li>
                  <li>Violates any applicable laws or regulations</li>
                </ul>
                <p className="mt-3">
                  You are responsible for ensuring your uploads comply with all applicable laws, including copyright and privacy laws.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">7. AI-Generated Content Disclaimer</h2>
                <p>
                  <strong>IMPORTANT:</strong> Our service uses artificial intelligence (AI) to generate transcripts and content. While we strive for accuracy and quality:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>AI-generated content may contain errors, inaccuracies, or omissions</li>
                  <li>Transcripts may not be 100% accurate and may contain transcription errors</li>
                  <li>Generated summaries, blog posts, and other content may not perfectly reflect the source material</li>
                  <li>AI output should be reviewed and verified before use or publication</li>
                </ul>
                <p className="mt-3">
                  <strong>You are solely responsible for reviewing, editing, and verifying all AI-generated content before using it for any purpose, including publication, distribution, or commercial use.</strong> EpisodeKit is not liable for any consequences arising from your use of AI-generated content without proper review and verification.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">8. Service Availability</h2>
                <p>
                  We strive to provide reliable service but do not guarantee uninterrupted, error-free, or secure operation. We may:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Suspend service for maintenance, updates, or technical issues</li>
                  <li>Experience downtime due to factors beyond our control</li>
                  <li>Modify, discontinue, or add features with or without notice</li>
                  <li>Impose additional restrictions or limitations on service usage</li>
                </ul>
                <p className="mt-3">
                  We will make reasonable efforts to notify users of planned maintenance or significant service changes.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">9. Limitation of Liability</h2>
                <p>
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>EpisodeKit is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, either express or implied</li>
                  <li>We disclaim all warranties, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement</li>
                  <li>We are not liable for any indirect, incidental, consequential, special, or punitive damages</li>
                  <li>Our total liability to you for any claims arising from your use of the service shall not exceed the amount you paid us in the 12 months preceding the claim</li>
                  <li>We are not responsible for the accuracy, quality, completeness, or reliability of AI-generated content</li>
                  <li>We are not liable for any damages arising from your reliance on AI-generated content without proper review</li>
                </ul>
                <p className="mt-3">
                  Some jurisdictions do not allow certain warranty exclusions or liability limitations, so some of these limitations may not apply to you.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">10. Prohibited Uses</h2>
                <p>
                  You agree not to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Use automated systems, bots, or scripts to access the service</li>
                  <li>Attempt to circumvent usage limits or security measures</li>
                  <li>Reverse engineer, decompile, or attempt to extract source code from our service</li>
                  <li>Resell, redistribute, or white-label our service or AI-generated content</li>
                  <li>Upload malicious files, viruses, or harmful code</li>
                  <li>Use the service for any illegal or unauthorized purpose</li>
                  <li>Interfere with or disrupt the service or servers</li>
                  <li>Violate any applicable laws or regulations</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">11. Termination</h2>
                <p>
                  We may suspend or terminate your account at any time, with or without cause, with or without notice, for violation of these Terms or for any other reason at our sole discretion. Upon termination:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Your right to use the service ceases immediately</li>
                  <li>You remain responsible for any outstanding fees or obligations</li>
                  <li>We may delete your account data and content in accordance with our data retention policies</li>
                </ul>
                <p className="mt-3">
                  You may terminate your account at any time by canceling your subscription and contacting us to request account deletion.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">12. Governing Law and Disputes</h2>
                <p>
                  These Terms are governed by applicable laws. Any disputes arising from these Terms or your use of the service shall be resolved in the courts of competent jurisdiction.
                </p>
                <p className="mt-3">
                  For users in the European Union, nothing in these Terms affects your statutory rights as a consumer under EU consumer protection law.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">13. Changes to Terms</h2>
                <p>
                  We may update these Terms at any time. We will notify you of material changes by email or through a notice on our website. Your continued use of the service after such notification constitutes acceptance of the updated Terms. If you do not agree to the updated Terms, you must stop using the service and cancel your subscription.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">14. Contact Information</h2>
                <p>
                  For questions about these Terms of Service, or for any other inquiries, contact us at:
                </p>
                <p className="mt-2">
                  <a href="mailto:support@episodekit.net" className="text-primary hover:underline">
                    support@episodekit.net
                  </a>
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Terms;